
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { MainNavigation } from '@/components/layout/MainNavigation';
import { Footer } from '@/components/layout/Footer';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RestrictedAccess = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);

  const { data: verification, isLoading } = useQuery({
    queryKey: ['peo-verification', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('peo_verifications')
        .select('*')
        .eq('user_id', user?.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  // If verification status is approved, redirect to dashboard
  useEffect(() => {
    if (verification?.status === 'approved' && !redirecting) {
      setRedirecting(true);
      console.log('RestrictedAccess: User is verified, redirecting to dashboard');
      navigate('/peo/dashboard', { replace: true });
    }
  }, [verification, navigate, redirecting]);

  // Show loading state when redirecting or loading verification data
  if (redirecting || isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <MainNavigation />
        <div className="flex-grow flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-lg">{redirecting ? 'Redirecting to dashboard...' : 'Checking verification status...'}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <MainNavigation />
      <div className="flex-grow pt-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <h1 className="text-3xl font-bold mb-6">PEO Provider Access</h1>
          
          {verification?.status === 'rejected' && (
            <Card className="border-destructive mb-6">
              <CardHeader>
                <CardTitle className="flex items-center text-destructive">
                  <AlertTriangle className="h-6 w-6 mr-2" />
                  Account Verification Failed
                </CardTitle>
                <CardDescription>
                  Unfortunately, your PEO provider account request was not approved
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  {verification.rejection_reason || 
                    "Your PEO provider verification was not approved. This may be because we couldn't verify your company information or credentials."}
                </p>
                <p className="text-sm font-medium">
                  Please contact our support team at support@thepeoapp.com for more information and assistance.
                </p>
              </CardContent>
            </Card>
          )}
          
          {(!verification || verification.status === 'pending') && (
            <Card className="border-orange-200 mb-6">
              <CardHeader>
                <CardTitle className="flex items-center text-orange-500">
                  <Clock className="h-6 w-6 mr-2" />
                  Verification Pending
                </CardTitle>
                <CardDescription>
                  Your account is currently under review
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Your PEO provider account is currently being reviewed by our administrative team. 
                  This process typically takes 1-2 business days.
                </p>
                <p className="text-sm">
                  We'll notify you by email once your account has been verified and you can access 
                  the full PEO provider dashboard. Thank you for your patience!
                </p>
              </CardContent>
            </Card>
          )}
          
          {verification?.status === 'approved' && (
            <Card className="border-green-200 mb-6">
              <CardHeader>
                <CardTitle className="flex items-center text-green-500">
                  <CheckCircle className="h-6 w-6 mr-2" />
                  Account Verified
                </CardTitle>
                <CardDescription>
                  Your account has been successfully verified
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Your PEO provider account has been verified. You can now access your dashboard 
                  and manage your PEO services.
                </p>
                <button 
                  onClick={() => navigate('/peo/dashboard', { replace: true })}
                  className="text-primary hover:underline font-medium flex items-center"
                >
                  Go to Dashboard →
                </button>
              </CardContent>
            </Card>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle>Verification Process</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-4 list-decimal list-inside">
                <li className="pl-2">
                  <span className="font-medium">Application Review</span>
                  <p className="text-muted-foreground ml-6 mt-1">
                    Our team reviews your application to verify your PEO provider credentials.
                  </p>
                </li>
                <li className="pl-2">
                  <span className="font-medium">Verification Checks</span>
                  <p className="text-muted-foreground ml-6 mt-1">
                    We validate your business information, licenses, and certifications.
                  </p>
                </li>
                <li className="pl-2">
                  <span className="font-medium">Account Activation</span>
                  <p className="text-muted-foreground ml-6 mt-1">
                    Once approved, your account will be activated, and you'll gain access to the PEO provider dashboard.
                  </p>
                </li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RestrictedAccess;
